import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, LogOut, RefreshCw, Copy, Check, Loader2, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MemberSpace = () => {
  const { lang, setLang, t } = useLang();
  const m = t.member;
  const [token, setToken] = useState(() => sessionStorage.getItem("prenet-member-token") || "");
  const [me, setMe] = useState(() => sessionStorage.getItem("prenet-member-username") || "");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ username: "", email: "" });

  const dn = useMemo(() => new Intl.DisplayNames([lang], { type: "region" }), [lang]);
  const countryName = (code) => (code ? dn.of(code) || code : "—");

  const logout = () => {
    sessionStorage.removeItem("prenet-member-token");
    sessionStorage.removeItem("prenet-member-username");
    setToken("");
    setMe("");
    setData(null);
    setStatus("idle");
  };

  const applyMemberLang = (code) => {
    if (code && LANGS.some((l) => l.code === code)) setLang(code);
  };

  const loadDownline = async (tok) => {
    setStatus("loading");
    try {
      const res = await fetch(`${API}/member/downline`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      const json = await res.json();
      setData(json);
      setMe(json.username);
      applyMemberLang(json.lang);
      setStatus("ready");
    } catch {
      setError(m.loginError);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (token) loadDownline(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/member/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: form.username, email: form.email }),
      });
      if (!res.ok) {
        setError(m.loginError);
        setStatus("error");
        return;
      }
      const json = await res.json();
      sessionStorage.setItem("prenet-member-token", json.access_token);
      sessionStorage.setItem("prenet-member-username", json.username);
      setToken(json.access_token);
      applyMemberLang(json.lang);
      await loadDownline(json.access_token);
    } catch {
      setError(m.loginError);
      setStatus("error");
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/r/${me}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString(lang, { day: "2-digit", month: "short", year: "numeric" });

  return (
    <main className="noise relative min-h-screen bg-night">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.14),transparent_55%)]" />
      <div className="relative z-10 mx-auto w-full max-w-3xl px-5 pb-24 pt-32 sm:px-8">
        {!token || status === "idle" ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass rounded-3xl p-7 sm:p-10"
            data-testid="member-login-card"
          >
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-violet-500/40 bg-violet-500/10">
              <Users size={26} className="text-violet-400" strokeWidth={1.6} />
            </div>
            <h1 className="mt-6 text-center font-display text-2xl font-bold text-ink sm:text-3xl" data-testid="member-login-title">
              {m.loginTitle}
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-center text-sm leading-relaxed text-ink-muted" data-testid="member-login-sub">
              {m.loginSub}
            </p>

            <form onSubmit={login} className="mx-auto mt-8 max-w-sm space-y-4">
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle" htmlFor="member-username">
                  {m.userLabel}
                </label>
                <input
                  id="member-username"
                  type="text"
                  required
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="mlmrockstar"
                  data-testid="member-login-username"
                  className="w-full rounded-xl border border-white/10 bg-night-2/80 px-4 py-3 text-sm text-ink placeholder:text-ink-subtle focus:border-signal/60 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle" htmlFor="member-email">
                  {m.emailLabel}
                </label>
                <input
                  id="member-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="vous@email.com"
                  data-testid="member-login-email"
                  className="w-full rounded-xl border border-white/10 bg-night-2/80 px-4 py-3 text-sm text-ink placeholder:text-ink-subtle focus:border-signal/60 focus:outline-none"
                />
              </div>
              {error && (
                <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="member-login-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="member-login-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-tactical via-signal to-tactical bg-[length:200%_100%] bg-left px-8 py-3.5 text-[13px] font-bold uppercase tracking-[0.08em] text-night transition-[background-position,box-shadow,transform] duration-300 hover:scale-[1.01] hover:bg-right hover:glow-signal disabled:opacity-60"
              >
                {status === "loading" ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                {m.loginBtn}
              </button>
            </form>

            <Link to="/" data-testid="member-back-link" className="mt-8 block text-center font-mono text-xs uppercase tracking-[0.15em] text-ink-subtle transition-colors hover:text-signal">
              ← PreNet
            </Link>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="member-dash"
          >
            <div className="glass rounded-3xl p-7 sm:p-9">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-violet-500/40 bg-violet-500/10">
                    <Users size={20} className="text-violet-400" />
                  </div>
                  <div>
                    <h1 className="font-display text-xl font-bold text-ink sm:text-2xl" data-testid="member-dash-title">{m.loginTitle}</h1>
                    <p className="font-mono text-xs tracking-[0.12em] text-violet-400" data-testid="member-dash-username">@{me}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => loadDownline(token)}
                    aria-label={m.refresh}
                    data-testid="member-refresh-button"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-ink-muted transition-colors hover:border-signal/40 hover:text-signal"
                  >
                    <RefreshCw size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={logout}
                    data-testid="member-logout-button"
                    className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-xs font-semibold text-ink-muted transition-colors hover:border-red-400/40 hover:text-red-300"
                  >
                    <LogOut size={14} />
                    <span className="hidden sm:inline">{m.logout}</span>
                  </button>
                </div>
              </div>

              {status === "ready" && data && (
                <>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <span className="rounded-full border border-signal2/40 bg-signal2/10 px-4 py-2 font-mono text-xs font-bold text-signal2" data-testid="member-count-badge">
                      {data.total} {m.directs}
                    </span>
                    {data.position !== null && data.position !== undefined && (
                      <span className="rounded-full border border-signal/30 bg-signal/10 px-4 py-2 font-mono text-xs font-bold text-signal" data-testid="member-position-badge">
                        {m.position} #{data.position}
                      </span>
                    )}
                  </div>

                  <p className="mt-5 text-sm leading-relaxed text-ink-muted" data-testid="member-dash-sub">
                    {m.dashSub}
                  </p>

                  <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-signal2/25 bg-night/70 p-4" data-testid="member-ref-box">
                    <p className="w-full font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">{m.yourLink}</p>
                    <p className="min-w-0 flex-1 truncate font-mono text-xs text-signal2" data-testid="member-ref-link">
                      {`${window.location.origin}/r/${me}`}
                    </p>
                    <button
                      type="button"
                      onClick={copyLink}
                      data-testid="member-copy-button"
                      className="flex items-center gap-1.5 rounded-full border border-signal2/40 px-4 py-2 text-[11px] font-bold text-signal2 transition-colors hover:bg-signal2 hover:text-night"
                    >
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                      {copied ? m.copied : m.copy}
                    </button>
                  </div>

                  {data.total === 0 ? (
                    <p className="mt-8 rounded-2xl border border-white/10 bg-night-2/60 p-8 text-center text-sm leading-relaxed text-ink-muted" data-testid="member-empty">
                      {m.noDirects}
                    </p>
                  ) : (
                    <div className="mt-6 overflow-hidden rounded-2xl border border-white/10" data-testid="member-table">
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[560px] text-left text-sm">
                          <thead className="border-b border-white/10 bg-night-3/60">
                            <tr className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">
                              <th className="px-3 py-3 sm:px-4">#</th>
                              <th className="px-3 py-3 sm:px-4">{m.colName}</th>
                              <th className="px-3 py-3 sm:px-4">{m.colEmail}</th>
                              <th className="px-3 py-3 sm:px-4">{m.colUsername}</th>
                              <th className="px-3 py-3 sm:px-4">{m.colCountry}</th>
                              <th className="px-3 py-3 sm:px-4">{m.colDate}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.members.map((k, i) => (
                              <tr key={k.username} className="border-b border-white/5 last:border-0" data-testid={`member-row-${k.username}`}>
                                <td className="px-3 py-3.5 font-mono text-xs font-bold text-signal sm:px-4">#{k.position ?? i + 1}</td>
                                <td className="px-3 py-3.5 font-medium text-ink sm:px-4">{k.name}</td>
                                <td className="px-3 py-3.5 text-xs text-ink-muted sm:px-4" data-testid={`member-email-${k.username}`}>{k.email}</td>
                                <td className="px-3 py-3.5 font-mono text-xs text-signal sm:px-4">@{k.username}</td>
                                <td className="px-3 py-3.5 text-xs text-ink-muted sm:px-4">
                                  {k.country ? (
                                    <span className="flex items-center gap-1.5">
                                      <img src={`/flags/${k.country.toLowerCase()}.png`} alt={k.country} className="h-3.5 w-5 rounded-[2px] object-cover" />
                                      {countryName(k.country)}
                                    </span>
                                  ) : "—"}
                                </td>
                                <td className="px-3 py-3.5 font-mono text-[11px] text-ink-subtle sm:px-4">{fmtDate(k.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <Link to="/" data-testid="member-back-link-bottom" className="mt-6 block text-center font-mono text-xs uppercase tracking-[0.15em] text-ink-subtle transition-colors hover:text-signal">
              ← PreNet
            </Link>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default MemberSpace;
